import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ClipboardList,
  FileSignature,
  FileText,
  HandCoins,
  History,
  ChevronRight,
} from "lucide-react";
import { fmtDateTime, fmtMoney } from "@/lib/format";

const NODE_ICON = {
  sale: ClipboardList,
  proforma: FileSignature,
  invoice: FileText,
  receipt: HandCoins,
};

/**
 * Compact consolidated view of everything linked to a sale:
 * pro-formas, invoices, receipts (via allocations), and the recent audit log.
 * Reads GET /api/sales/{sid}/chain — this endpoint does not mutate anything.
 */
export function SaleChain({ saleId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!saleId) return;
    let cancelled = false;
    setLoading(true); setError("");
    api.get(`/sales/${saleId}/chain`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => { if (!cancelled) setError(formatApiError(e.response?.data?.detail) || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [saleId]);

  if (loading) return <p className="text-xs text-slate-500" data-testid="chain-loading">Loading chain…</p>;
  if (error) return <p role="alert" className="text-xs text-rose-800" data-testid="chain-error">{error}</p>;
  if (!data) return null;

  const { proformas = [], invoices = [], receipts = [], allocations = [], audit = [] } = data;

  return (
    <div className="border border-slate-200 rounded p-3 bg-slate-50 space-y-3" data-testid="sale-chain">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
        <History className="w-3 h-3" /> Chain of records
      </div>

      <Group icon={NODE_ICON.proforma} title="Pro-formas" count={proformas.length} testId="chain-proformas">
        {proformas.map((p) => (
          <ChainRow key={p.id} testId={`chain-proforma-${p.id}`}
                    number={p.number} status={p.status}
                    right={<><span className="font-mono">{fmtMoney(p.total_minor, p.currency)}</span></>}
                    linkTo={`/proformas?id=${p.id}`} />
        ))}
      </Group>

      <Group icon={NODE_ICON.invoice} title="Invoices" count={invoices.length} testId="chain-invoices">
        {invoices.map((i) => (
          <ChainRow key={i.id} testId={`chain-invoice-${i.id}`}
                    number={i.number || `DRAFT-${i.id.slice(0, 6)}`} status={i.status}
                    right={<>
                      <span className="font-mono">{fmtMoney(i.total_minor, i.currency)}</span>
                      {i.payment_status && <span className="ml-2 text-[10px] uppercase text-slate-500">{i.payment_status}</span>}
                    </>}
                    linkTo={`/invoices?id=${i.id}`} />
        ))}
      </Group>

      <Group icon={NODE_ICON.receipt} title="Receipts" count={receipts.length} testId="chain-receipts">
        {receipts.map((r) => (
          <ChainRow key={r.id} testId={`chain-receipt-${r.id}`}
                    number={r.bank_ref || r.provider_txn_id || r.id.slice(0, 8)} status={r.status}
                    right={<span className="font-mono">{fmtMoney(r.amount_minor, r.currency)}</span>}
                    linkTo={`/receipts?id=${r.id}`} />
        ))}
      </Group>

      {allocations.length > 0 && (
        <details className="text-xs" data-testid="chain-allocations">
          <summary className="cursor-pointer text-slate-500">Allocations ({allocations.length})</summary>
          <ul className="mt-1 space-y-0.5">
            {allocations.map((a) => (
              <li key={a.id} className="font-mono text-[11px] text-slate-600">
                {fmtMoney(a.amount_minor, "MYR")} · receipt {a.receipt_id?.slice(0, 8)} → invoice {a.invoice_id?.slice(0, 8) || "—"}
                {a.reversed && <span className="ml-2 text-rose-700">REVERSED</span>}
              </li>
            ))}
          </ul>
        </details>
      )}

      {audit.length > 0 && (
        <details className="text-xs" data-testid="chain-audit">
          <summary className="cursor-pointer text-slate-500">Audit log ({audit.length})</summary>
          <ul className="mt-1 space-y-0.5 max-h-48 overflow-y-auto">
            {audit.map((r) => (
              <li key={r.id} className="text-[11px] flex flex-wrap gap-x-2">
                <span className="font-mono text-slate-500">{fmtDateTime(r.at)}</span>
                <span className="text-slate-800">{r.action}</span>
                <span className="text-slate-500">by</span>
                <span>{r.actor_email || r.actor_id?.slice(0, 8)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Group({ icon: Icon, title, count, children, testId }) {
  if (count === 0) {
    return (
      <div className="text-xs text-slate-400" data-testid={`${testId}-empty`}>
        <Icon className="inline w-3 h-3 mr-1" /> {title}: none
      </div>
    );
  }
  return (
    <div data-testid={testId}>
      <div className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-1">
        <Icon className="w-3 h-3" /> {title} <span className="text-slate-400 font-normal">({count})</span>
      </div>
      <div className="space-y-1 pl-4">{children}</div>
    </div>
  );
}

function ChainRow({ number, status, right, linkTo, testId }) {
  return (
    <a href={linkTo} className="flex items-center gap-2 text-xs hover:bg-white rounded px-1.5 py-1 -mx-1.5" data-testid={testId}>
      <ChevronRight className="w-3 h-3 text-slate-400" />
      <span className="font-mono">{number}</span>
      {status && <Badge variant="outline" className="text-[10px] uppercase">{status}</Badge>}
      <div className="ml-auto text-slate-600">{right}</div>
    </a>
  );
}
